<template>
  <BModal
    ref="modalMemo"
    scrollable
    centered
    no-close-on-esc
    no-enforce-focus
    no-close-on-backdrop
    aria-hidden="false"
    role="dialog"
    :title="MMemoTitle"
  >
    <ValidationObserver
      ref="formMemo"
    >
      <form
        role="form"
        novalidate
        @submit.prevent
      >
        <BRow>
          <BCol
            cols="12"
          >
            <ValidationProvider
              v-slot="{ errors }"
              vid="month"
              name="month"
              rules="required"
            >
              <BFormGroup
                label="Month"
                label-for="fMonth"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <flat-pickr
                  id="fMonth"
                  v-model="formMemo.schedule"
                  :class="['form-control', {
                    'is-invalid': errors.length > 0
                  }]"
                  placeholder="--------- ----"
                  :config="{
                    ...flatPickerConfig,
                    altInput: false
                  }"
                  :disabled="state.busy"
                />
                <div
                  v-if="(errors.length > 0)"
                  class="invalid-feedback"
                  v-text="errors[0]"
                />
              </BFormGroup>
            </ValidationProvider>
          </BCol>

          <BCol
            cols="12"
          >
            <ValidationProvider
              v-slot="{ errors }"
              vid="memo_id"
              name="memo"
              rules="required"
            >
              <BFormGroup
                label="Memo"
                label-for="fMemo"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <VSelect
                  v-model="selected.memo"
                  input-id="fMemo"
                  label="memo_name"
                  clearable
                  append-to-body
                  placeholder="select memo here"
                  :options="options.memos"
                  disabled
                  :loading="fetching.memos"
                  :class="{ 'is-invalid': errors.length > 0 }"
                  @search="searchMemos"
                >
                  <template #option="{ memo_name }">
                    <div
                      class="text-break text-wrap"
                      v-text="memo_name"
                    />
                  </template>
                  <template #selected-option="{ memo_name }">
                    <div
                      class="w-225px w-md-325px text-truncate"
                      v-text="memo_name"
                    />
                  </template>
                  <template #no-options="">
                    no available memo
                  </template>
                </VSelect>
                <div
                  v-if="(errors.length > 0)"
                  class="invalid-feedback"
                  v-text="errors[0]"
                />
              </BFormGroup>
            </ValidationProvider>
          </BCol>

          <BCol
            cols="12"
          >
            <ValidationProvider
              v-slot="{ errors }"
              vid="officer"
              name="officer"
              rules="required"
            >
              <BFormGroup
                label="Officer"
                label-for="fOfficer"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <VSelect
                  v-model="selected.fsOfficer"
                  input-id="fOfficer"
                  label="officer_name"
                  clearable
                  append-to-body
                  placeholder="select officer here"
                  :options="options.fsOfficers"
                  :disabled="[
                    state.busy,
                    fetching.fsOfficers,
                    state.editing
                  ].some(isTrue => isTrue)"
                  :loading="fetching.fsOfficers"
                  :calculate-position="calculateDropPosition"
                  :class="{ 'is-invalid': errors.length > 0 }"
                  @search="searchFSOfficers"
                >
                  <template #option="{ officer_name, sso_id }">
                    <div
                      class="text-break text-wrap"
                    >
                      <strong class="text-primary">{{ sso_id }}</strong> - {{ officer_name }}
                    </div>
                  </template>
                  <template #selected-option="{ officer_name, sso_id }">
                    <div
                      class="text-break text-wrap"
                    >
                      <strong class="text-primary">{{ sso_id }}</strong> - {{ officer_name }}
                    </div>
                  </template>
                  <template #no-options="">
                    no available officer
                  </template>
                </VSelect>
                <div
                  v-if="(errors.length > 0)"
                  class="invalid-feedback"
                  v-text="errors[0]"
                />
              </BFormGroup>
            </ValidationProvider>
          </BCol>

          <BCol
            cols="12"
          >
            <ValidationProvider
              v-slot="{ errors }"
              vid="cluster"
              name="cluster"
              rules="required"
            >
              <BFormGroup
                label="Cluster"
                label-for="fCluster"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <BFormSelect
                  id="fCluster"
                  v-model="selected.cluster.id"
                  value-field="id"
                  text-field="cluster_name"
                  :options="selected.fsOfficer?.clusters || []"
                  :state="errors.length > 0 ? false : null"
                  :disabled="state.busy || fetching.fsOfficers"
                >
                  <template #first>
                    <BFormSelectOption
                      value=""
                      disabled
                      v-text="' - - select here - - '"
                    />
                  </template>
                </BFormSelect>
                <div
                  v-if="(errors.length > 0)"
                  class="invalid-feedback"
                  v-text="errors[0]"
                />
              </BFormGroup>
            </ValidationProvider>
          </BCol>

          <BCol
            cols="12"
          >
            <ValidationProvider
              v-slot="{ errors }"
              vid="customer_id"
              name="customer"
              rules="required"
            >
              <BFormGroup
                label="Customer"
                label-for="fCustomer"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <VSelect
                  v-model="selected.customer"
                  input-id="fCustomer"
                  label="customer_name"
                  append-to-body
                  placeholder="select customer here"
                  :options="options.customers"
                  :disabled="[
                    fetching.customers,
                    fetching.fsOfficers,
                    state.busy
                  ].some(isTrue => isTrue)"
                  :loading="fetching.customers"
                  :class="{ 'is-invalid': errors.length > 0 }"
                  @search="searchCustomers"
                >
                  <template #option="{ customer_name, mobile_number, address }">
                    <div class="d-flex flex-column flex-md-row py-1">
                      <div class="flex-grow-1 d-flex align-items-center">
                        <div class="mr-2">
                          <BAvatar
                            :text="String(customer_name).charAt(0)"
                            variant="primary"
                            size="50px"
                          />
                        </div>
                        <div class="d-flex flex-column">
                          <div
                            class="text-break text-wrap font-weight-bolder"
                            v-text="customer_name"
                          />
                          <strong class="mb-2">
                            {{ mobile_number }}
                          </strong>
                          <div
                            v-if="address"
                            class="text-break text-wrap text-muted small"
                          >
                            {{ address }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                  <template #selected-option="{ customer_name }">
                    <div class="d-flex flex-column flex-md-row">
                      <div class="flex-grow-1 d-flex align-items-center">
                        <div class="mr-2">
                          <BAvatar
                            :text="String(customer_name).charAt(0)"
                            variant="primary"
                            size="28px"
                          />
                        </div>
                        <div
                          class="w-200px w-lg-300px text-truncate"
                          v-text="customer_name"
                        />
                      </div>
                    </div>
                  </template>
                  <template #no-options="">
                    no available customer
                  </template>
                </VSelect>
                <div
                  v-if="(errors.length > 0)"
                  class="invalid-feedback"
                  v-text="errors[0]"
                />
              </BFormGroup>
            </ValidationProvider>
          </BCol>

        </BRow>

      </form>
    </ValidationObserver>
    <template #modal-footer="{ cancel }">
      <BButton
        variant="primary"
        :disabled="state.busy"
        @click.prevent="validateMemo"
      >
        {{ state.editing ? 'Update' : 'Create' }} Memo
      </BButton>
      <BButton
        variant="outline-dark"
        :disabled="state.busy"
        @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
      >
        Discard
      </BButton>
    </template>

  </BModal>
</template>
<script>
import { find } from 'lodash'
import { mapGetters } from 'vuex'
import FPMonthSelect from 'flatpickr/dist/plugins/monthSelect'
import moment from 'moment'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MFSOfficers from '@/mixins/accounts/fsos'
import MCustomers from '@/mixins/accounts/customers'
import MMemos from '@/mixins/maintenances/memos'

import { SASMFsoCustomerMemo } from '@/services/asm'

export default {
  name: 'ASMCustomerMemosCrud',

  mixins: [
    MxMisc,
    MxFormatter,
    MFSOfficers,
    MCustomers,
    MMemos
  ],

  emits: [
    'update:row',
    'update:rows'
  ],

  data () {
    return {
      flatPickerConfig: {
        appendTo: document.body,
        altInput: true,
        allowInput: true,
        disableMobile: true,
        plugins: [
          new FPMonthSelect({
            dateFormat: 'Y-m',
            altFormat: 'F Y'
          })
        ]
      },
      state: {
        busy: false,
        editing: false
      },
      selected: {
        cluster: {
          id: ''
        }
      },
      formMemo: {
        id: '',
        schedule: '',
        assignee_id: '',
        customer_id: '',
        memo_id: '',
        status: 'Pending',
        verification_status: 'Pending'
      }
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'auth/user'
    }),

    MMemoTitle () {
      return this.state.editing ? 'Edit Memos' : 'Create Memos'
    }
  },

  watch: {
    'selected.fsOfficer' (officer) {
      this.formMemo.assignee_id = officer?.id || ''
    },

    'selected.customer' (customer) {
      this.formMemo.customer_id = customer?.id || ''
    },

    'selected.memo' (memo) {
      this.formMemo.memo_id = memo?.id || ''
    },

    'selected.cluster.id' (cluster) {
      if (cluster) {
        this.getCustomers(this).finally(() => {
          if (this.editable.customer) {
            this.selected.customer = find(this.options.customers, {
              id: this.editable.customer
            }) || ''

            this.editable.customer = ''
          }
        })
      } else {
        this.selected.customer = ''
        this.options.customers = []
      }
    }
  },

  methods: {

    createMemo () {
      this.state.editing = false

      this.formMemo = Object.assign(this.formMemo, {
        id: '',
        schedule: moment().format('YYYY-MM'),
        assignee_id: '',
        customer_id: '',
        memo_id: '',
        status: 'Pending',
        verification_status: 'Pending'
      })

      this.getMemos(this).finally(() => {
        this.getFSOfficers(this, '', this.currentUser?.team, this.currentUser?.area)
      })

      this.$refs.modalMemo.show()
    },

    editMemo (memo) {
      this.state.editing = true

      this.selected.cluster.id = ''
      this.editable.customer = memo?.customer_id || ''

      this.formMemo = Object.assign(this.formMemo, {
        id: memo.id,
        schedule: memo.schedule,
        assignee_id: '',
        customer_id: '',
        memo_id: '',
        status: 'Pending',
        verification_status: 'Pending'
      })

      this.getMemos(this).finally(() => {
        this.selected.memo = find(this.options.memos, {
          id: memo?.memo_id || ''
        }) || ''

        this.getFSOfficers(this, '', this.currentUser?.team, this.currentUser?.area).finally(
          () => {
            this.selected.cluster.id = memo?.customer?.cluster_id || ''

            this.selected.fsOfficer = find(this.options.fsOfficers, {
              id: memo?.assignee_id || ''
            }) || ''
          }
        )
      })

      this.$refs.modalMemo.show()
    },

    validateMemo () {
      this.$refs.formMemo.validate().then(
        validMemo => {
          if (!validMemo) {
            return this.swalInvalid()
          }

          return this.swalConfirm({
            html: `${this.state.editing ? 'Update' : 'Create'} this Memo?`,
            confirmButtonText: this.state.editing ? 'Update' : 'Create',
            preConfirm: () => {
              this.state.busy = true

              const createOrUpdate = () => {
                return this.state.editing ? this.putMemo() : this.postMemo()
              }

              return createOrUpdate().catch(
                ({ message }) => {
                  if (message && message?.error) {
                    this.$refs.formMemo.setErrors(message.error)
                  }
                }
              ).finally(() => {
                this.state.busy = false
              })
            }
          })
        }
      )
    },

    async postMemo () {
      return SASMFsoCustomerMemo.post(this.formMemo).then(
        ({ data }) => {
          this.$refs.modalMemo.hide()

          this.swalSuccess(data.message).then(() => {
            this.$emit('update:rows')
          })
        }
      )
    },

    async putMemo () {
      return SASMFsoCustomerMemo.put(this.formMemo).then(
        ({ data }) => {
          this.$refs.modalMemo.hide()

          this.swalSuccess(data.message).then(() => {
            this.$emit('update:row', data.customer_memo)
          })
        }
      )
    }
  }
}
</script>
